import { useState } from 'react';
import { displayValue, Field, validateFields, visibleFields, visibleValues, type FieldDef } from '../../ui/Fields';
import { useToast } from '../../ui/Toasts';
import { errorMessage } from '../../app/api';
import type { RegistryEntry, WidgetComponentProps } from '../types';

interface Step {
  id: string;
  title: string;
  fields?: FieldDef[];
  review?: boolean;
  note?: string;
}

function initial(steps: Step[]) {
  const v: Record<string, any> = {};
  for (const s of steps) for (const f of s.fields ?? []) v[f.name] = f.default ?? (f.type === 'multiselect' ? [] : '');
  return v;
}

function Wizard({ props, data, ctx }: WidgetComponentProps) {
  const toast = useToast();
  const steps: Step[] = props.steps;
  const [i, setI] = useState(0);
  const [values, setValues] = useState<Record<string, any>>(() => initial(steps));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const step = steps[i];
  const allFields = steps.flatMap((s) => s.fields ?? []);
  const options = data?.options;

  const next = () => {
    setResult(null);
    const problem = validateFields(step.fields ?? [], values);
    if (problem) return setError(problem);
    setError(null);
    setI((x) => Math.min(steps.length - 1, x + 1));
  };
  const [result, setResult] = useState<string | null>(null);
  const submit = async () => {
    const problem = validateFields(allFields, values);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      const res = await ctx.action(props.submitAction, null, visibleValues(allFields, values));
      toast(String(res.message ?? 'Submitted').split('\n')[0], 'success');
      setResult(res.message ?? 'Done');
      setValues(initial(steps));
      setI(0);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <ol className="steps" aria-label="Steps" style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
        {steps.map((s, k) => (
          <li key={s.id} className={`step${k === i ? ' on' : ''}`} aria-current={k === i ? 'step' : undefined}>
            {k + 1}. {s.title}
          </li>
        ))}
      </ol>
      <div className="form-grid" style={{ flex: 1, overflow: 'auto' }}>
        {result && (
          <div className="w-note" role="status" style={{ whiteSpace: 'pre-line', margin: 0 }}>
            <span>{result}</span>
            <button className="btn sm ghost" style={{ marginLeft: 'auto' }} onClick={() => setResult(null)}>
              Dismiss
            </button>
          </div>
        )}
        {visibleFields(step.fields ?? [], values).map((f) => <Field key={f.name} f={f} value={values[f.name]} onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} options={options} />)}
        {step.review && (
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', margin: 0 }}>
            {visibleFields(allFields, values).map((f) => (
              <div key={f.name} style={{ display: 'contents' }}>
                <dt className="muted">{f.label}</dt>
                <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{displayValue(f, values[f.name], options)}</dd>
              </div>
            ))}
          </dl>
        )}
        {step.note && <div className="hint">{step.note}</div>}
        {error && <div className="error-text" role="alert" style={{ whiteSpace: 'pre-line' }}>{error}</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 10 }}>
        {i > 0 && (
          <button className="btn" onClick={() => setI((x) => x - 1)}>
            Back
          </button>
        )}
        {i < steps.length - 1 ? (
          <button className="btn primary" onClick={next}>
            Next
          </button>
        ) : (
          <button className="btn primary" disabled={busy} onClick={submit}>
            {busy ? 'Working…' : props.submitLabel ?? 'Submit'}
          </button>
        )}
      </div>
    </div>
  );
}

export const wizard: RegistryEntry = { component: Wizard };
